import type { ComponentRef, Injector, Type } from '@angular/core';
import { ApplicationRef, createComponent, DestroyRef, EnvironmentInjector } from '@angular/core';

import type {
  TooltipOption,
  TooltipPositionCallback,
  TopLevelFormatterParams,
} from 'echarts/types/dist/shared';

const TOOLTIP_CURSOR_OFFSET = 12;

type ChartTooltipPlacement = 'top' | 'right';

// Renders an Angular component as an ECharts tooltip. The formatter is called on
// every mouse move, so the rendered component is memoized by its inputs and
// destroyed together with the host. Pass the presentational component per chart
// type (answers, skills, ratings, ...) and map the hovered params to its inputs.
// `placement` may be a getter so it can follow reactive chart state (e.g. switch
// to 'right' once a chart has enough options to render horizontal bars).
export const createChartTooltip = <T>(config: {
  injector: Injector;
  component: Type<T>;
  inputs: (params: TopLevelFormatterParams) => Record<string, unknown>;
  placement?: ChartTooltipPlacement | (() => ChartTooltipPlacement);
}): TooltipOption => {
  let memoized: {
    key: string;
    component: ComponentRef<T>;
    el: HTMLElement;
  } | null = null;

  const destroyMemoized = (): void => {
    memoized?.component.destroy();
    memoized?.el.remove();
    memoized = null;
  };

  config.injector.get(DestroyRef).onDestroy(destroyMemoized);

  return {
    show: true,
    trigger: 'axis',
    axisPointer: { type: 'none' },
    transitionDuration: 0,
    padding: 0,
    borderWidth: 0,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    shadowColor: 'none',
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    appendTo: () => {
      const rootComponent = config.injector.get(ApplicationRef).components[0];
      return rootComponent ? rootComponent.location.nativeElement : document.body;
    },
    extraCssText: 'z-index: 2147483000',
    position: ((point, _params, _dom, _rect, size) => {
      const [pointerX, pointerY] = point;
      const [contentWidth, contentHeight] = size.contentSize;
      const placement =
        typeof config.placement === 'function' ? config.placement() : config.placement;

      if (placement === 'right') {
        return [pointerX + TOOLTIP_CURSOR_OFFSET, pointerY - contentHeight / 2];
      }

      return [pointerX - contentWidth / 2, pointerY - contentHeight - TOOLTIP_CURSOR_OFFSET];
    }) satisfies TooltipPositionCallback,
    formatter: (params: TopLevelFormatterParams) => {
      const inputs = config.inputs(params);
      const key = JSON.stringify(inputs);

      if (memoized?.key === key) {
        return memoized.el;
      }
      destroyMemoized();

      const el = document.createElement('div');
      const component = createComponent(config.component, {
        hostElement: el,
        environmentInjector: config.injector.get(EnvironmentInjector),
      });
      Object.entries(inputs).forEach(([name, value]) => component.setInput(name, value));
      config.injector.get(ApplicationRef).attachView(component.hostView);
      component.changeDetectorRef.detectChanges();

      memoized = { key, component, el };

      return el;
    },
  };
};
